import { Text, HStack, VStack } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { MethodClassIcon } from "components/Icons";
import { useCurrencyFormatter } from "components/FormattedMoney/useCurrencyFormatter";

const BankRailOption = ({option, onPayoutMethodSelection, invalidTotal, invalidReasonCode, payoutFromCurrency, payoutFromAmount }) => {
  const bankRailDisplayCurrency = option.currency || "USD";
  const formatter = useCurrencyFormatter(bankRailDisplayCurrency);
  const fromCurrencyFormatter = useCurrencyFormatter(payoutFromCurrency || "USD"); // since bank rail option data should always be in USD, this is to handle if the original currency was not USD (as in, virtual currencies)
  const { invalid_amount, bank_currency_unsupported } = option;
  let disableMsg = null;
  const isDisabled = invalidTotal || invalid_amount;
  if (invalidTotal) {
    // invalidTotal should mean the payout is not within limits (and should include an invalidReasonCode)
    // set a default first in case we don't know the code
    let msg = (
      <FormattedMessage
        defaultMessage="Invalid payout method"
        description="default error message is we have no reason code"
      />
    );
    if (invalidReasonCode === "PAYOUT_AMOUNT_TOO_HIGH") {
      msg = (
        <FormattedMessage
          defaultMessage="Exceeds maximum amount"
          description="request amount exceeds limit error message"
        />
      );
    } else if (invalidReasonCode === "PAYOUT_AMOUNT_TOO_LOW") {
      msg = (
        <FormattedMessage
          defaultMessage="Below minimum amount"
          description="request amount is below min limit error message"
        />
      );
    }
    disableMsg = <Text as="i" color="errorText" fontSize="sm">{msg}</Text>;
  } else if (invalid_amount) {
    // invalid_amount is currently only used to say the fees are enough to make the payout go negative (not happy there isn't a reason code in this case, but was asked for and shot down)
    disableMsg = (
      <Text as="i" color="errorText" fontSize="sm">
        <FormattedMessage defaultMessage="Invalid amount" description="error message" />
      </Text>
    );
  }
  const handleClick = () => {
    if (!isDisabled && onPayoutMethodSelection) {
      onPayoutMethodSelection(option);
    }
  }
  return (
    <HStack
      width="100%"
      justify="space-between"
      p="4"
      borderWidth="1px"
      borderRadius="4px"
      onClick={handleClick}
      opacity={isDisabled ? 0.5 : 1}
      pointerEvents={isDisabled ? "none" : "all"}
      boxShadow="base"
      cursor="pointer"
      data-testid={`bank-rail-option-${option.id}`}
      _hover={{
        bgColor: "lightBackground",
        borderColor: "integrator.300",
      }}
    >
      <HStack flex="1">
        <MethodClassIcon name={option.name} />
        <Text pl="1" fontSize="sm" textTransform="uppercase">
          {option.name}
        </Text>
      </HStack>
      <VStack spacing="0" align="flex-end" flex="1">
        <Text fontSize="xs" color="secondaryText" textTransform="uppercase">
          <FormattedMessage
            defaultMessage={"Requested: {amount}"}
            description="amount user requested"
            values={{
              amount: formatter(option.payout_requested),
            }}
          />
          {(payoutFromCurrency !== bankRailDisplayCurrency) && payoutFromAmount && (
            <Text as="i">&nbsp;({fromCurrencyFormatter(payoutFromAmount)})</Text>
          )}
        </Text>
        <Text fontSize="xs" color="secondaryText" textTransform="uppercase">
          <FormattedMessage
            defaultMessage={"Fees <i>(estimated)</i>: ~{amount}"}
            description="fees description"
            values={{
              amount: formatter(option.payout_fee),
              i: (txt) => <i>{txt}</i>
            }}
          />
        </Text>
        {option.is_variable_fee && (
          <Text as="i" fontSize="xs" color="secondaryText">
            <FormattedMessage
              defaultMessage="(estimated fees, amount may change)"
              description="describes how fee is variable and what is shown is just an estimate"
            />
          </Text>
        )}
        <Text fontSize="sm" textTransform="uppercase">
          <FormattedMessage
            defaultMessage={"Payout total: {amount}"}
            description="amount to be paid out to the user"
            values={{
              amount: formatter(option.payout_total),
            }}
          />
        </Text>
        {disableMsg}
        {bank_currency_unsupported && (
          <Text as="i" color="errorText" fontSize="xs" textAlign="right">
            <FormattedMessage 
              defaultMessage="This option requires the destination bank to support USD currency. If it does not the payout may fail."
              description="Unsupported currency user message"
            />
          </Text>
        )}
      </VStack>
    </HStack>
  );
};

export default BankRailOption;
